/**
 * Global WebSocket Service - ITP CRM Platform
 * Maintains persistent connection throughout user session
 * Handles real-time events like ticket updates, customer responses, notifications, etc.
 */

// WebSocket configuration
const WS_CONFIG = {
  url: "wss://ws.cloud.itp360.com/ws",
  pingInterval: 30000, // 30 seconds
  reconnectInterval: 10000, // 10 seconds
  maxRetries: 5,
  retryBackoffMultiplier: 1.5,
  maxRetryDelay: 30000 // 30 seconds max
};

const DEFAULT_ACCOUNT_ID = "itpcrm_owner";

class GlobalWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.retryCount = 0;
    this.reconnectTimeout = null;
    this.pingInterval = null;
    this.eventListeners = new Map(); // Custom event listeners
    this.connectionListeners = new Set(); // Connection status listeners
    this.shouldReconnect = true;

    // Bind methods to maintain context
    this.handleOpen = this.handleOpen.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleError = this.handleError.bind(this);
    this.sendPing = this.sendPing.bind(this);
  }

  /**
   * Connect to WebSocket server
   * @param {Object} authData - Authentication data (token, user_id, account_id, etc.)
   */
  connect(authData = {}) {
    if (this.isConnecting || this.isConnected) {
      console.log("🔌 WebSocket already connecting or connected");
      return;
    }

    // Get auth data from localStorage if not provided
    const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
    const token = authData.token || userDetails.access_token;
    const userId = authData.user_id || userDetails.pk;
    const accountId = authData.account_id || userDetails.account_id || DEFAULT_ACCOUNT_ID;

    if (!token || !userId) {
      console.warn("⚠️ Missing authentication data for WebSocket connection");
      return;
    }

    this.isConnecting = true;
    this.shouldReconnect = true;

    // Store auth data for reconnections
    this.authData = { token, user_id: userId, account_id: accountId };

    try {
      // Close existing socket if any (without triggering full disconnect flow)
      if (this.socket) {
        // Remove event handlers to prevent them from firing during replacement
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.close();
        this.socket = null;
      }

      // Stop ping interval from old connection
      this.stopPingInterval();

      // Clear any pending reconnection timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      // Create new WebSocket connection with authentication parameters in URL
      const wsUrl = `${WS_CONFIG.url}?token=${encodeURIComponent(this.authData.token)}&service=itpcrm&account_id=${this.authData.account_id || DEFAULT_ACCOUNT_ID}&user_id=${this.authData.user_id}`;

      console.log("🔌 Connecting to WebSocket:", wsUrl.replace(this.authData.token, '***'));

      this.socket = new WebSocket(wsUrl);

      // Attach event handlers
      this.socket.onopen = this.handleOpen;
      this.socket.onmessage = this.handleMessage;
      this.socket.onclose = this.handleClose;
      this.socket.onerror = this.handleError;

      // Start ping interval immediately after creating WebSocket
      this.startPingInterval();

    } catch (error) {
      console.error("❌ Error creating WebSocket connection:", error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket connection open
   */
  handleOpen(event) {
    console.log("✅ WebSocket connected successfully");
    this.isConnected = true;
    this.isConnecting = false;
    this.retryCount = 0;

    // Notify connection listeners
    this.notifyConnectionListeners("connected");
  }

  /**
   * Extract the actual event payload from the itp-ws message envelope.
   *
   * Messages published through the ITPCrmHandler arrive double-wrapped:
   *   { type, data: { content: { type, data: {actual payload} }, source_service } }
   *
   * This helper unwraps that so event listeners get the flat payload directly.
   */
  _extractPayload(rawData) {
    if (rawData?.content?.data && typeof rawData.content.data === "object") {
      return rawData.content.data;
    }
    return rawData;
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      console.log("📨 WebSocket message received:", data.type, data);

      // Extract the actual payload from itp-ws envelope
      const payload = this._extractPayload(data.data);

      // Handle different message types
      switch (data.type) {
        case "ping":
          // Respond to server ping with pong
          this.sendPong();
          break;

        case "connection.established":
          console.log("✅ WebSocket connection established");
          break;

        case "auth_success":
          console.log("✅ WebSocket authentication successful");
          break;

        case "auth_error":
        case "error":
          console.error("❌ WebSocket error:", data);
          break;

        // CRM-specific events
        case "ticket_assigned":
          this.dispatchCustomEvent("ticket_assigned", payload);
          this.showNotification("Ticket Assigned", `Ticket #${payload?.ticket_id} assigned to you`, "info");
          break;

        case "customer_response":
          this.dispatchCustomEvent("customer_response", payload);
          this.showNotification("Customer Response", `${payload?.customer_name} responded to Ticket #${payload?.ticket_id}`, "info");
          break;

        case "ticket_updated":
          this.dispatchCustomEvent("ticket_updated", payload);
          break;
        case "ticket_event":
          this.dispatchCustomEvent("ticket_event", payload);
          break;

        case "ai_agent_run_event":
          // Live push from the AI Agents runtime. The RunDetailModal
          // listens and merges new events into its timeline without
          // waiting for the next poll.
          this.dispatchCustomEvent("ai_agent_run_event", payload);
          break;

        case "incoming_call":
          this.dispatchCustomEvent("incoming_call", payload);
          break;

        case "notification":
          this.dispatchCustomEvent("notification", payload);
          this.showNotification(payload?.title || "Notification", payload?.message, payload?.notification_type || "info");
          break;

        case "alert":
          this.handleAlert(payload);
          break;

        default:
          // Dispatch generic event for unknown message types
          this.dispatchCustomEvent(data.type, payload);
          break;
      }

    } catch (error) {
      console.error("❌ Error parsing WebSocket message:", error);
    }
  }

  /**
   * Handle WebSocket connection close
   */
  handleClose(event) {
    console.log("🔌 WebSocket connection closed:", event.code, event.reason);
    this.isConnected = false;
    this.isConnecting = false;
    this.stopPingInterval();

    // Notify connection listeners
    this.notifyConnectionListeners("disconnected");

    // Schedule reconnection if needed
    if (this.shouldReconnect) {
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket errors
   */
  handleError(error) {
    console.error("❌ WebSocket error:", error);
    this.isConnected = false;
    this.isConnecting = false;

    // Notify connection listeners
    this.notifyConnectionListeners("error");
  }

  /**
   * Handle alert messages from WebSocket
   * @param {Object} alertData - Alert data from server
   */
  handleAlert(alertData) {
    try {
      // Handle nested alert structure
      let actualAlertData = alertData;

      if (alertData.content && alertData.content.data) {
        actualAlertData = alertData.content.data;
      }

      const { subject, message, severity, category, action, metadata } = actualAlertData;

      // Show notification
      this.showNotification(
        subject || "System Alert",
        message || "No message provided",
        severity || "info"
      );

      // Dispatch custom event for components that need to handle alerts
      this.dispatchCustomEvent("alert", actualAlertData);

    } catch (error) {
      console.error("❌ Error handling alert:", error);
    }
  }

  /**
   * Show browser notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {string} type - Notification type (info, success, warning, error)
   */
  showNotification(title, message, type = "info") {
    // Dispatch event for notification system to handle
    this.dispatchCustomEvent("show_notification", { title, message, type });

    // Also try to show browser notification if permission granted
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        new Notification(title, {
          body: message,
          icon: "/favicon.ico",
          tag: `itpcrm-${Date.now()}`
        });
      } catch (error) {
        console.error("❌ Error showing browser notification:", error);
      }
    }
  }

  /**
   * Start ping interval for keep alive mechanism
   */
  startPingInterval() {
    // Clear existing interval
    this.stopPingInterval();

    // Start new ping interval
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, WS_CONFIG.pingInterval);
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Send ping message to server
   */
  sendPing() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const pingMessage = {
        type: "ping",
        timestamp: new Date().toISOString(),
        version: "1.0"
      };

      this.socket.send(JSON.stringify(pingMessage));
    }
  }

  /**
   * Send pong response to server ping
   */
  sendPong() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const pongMessage = {
        type: "pong",
        timestamp: new Date().toISOString(),
        version: "1.0"
      };

      this.socket.send(JSON.stringify(pongMessage));
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    if (!this.shouldReconnect || this.retryCount >= WS_CONFIG.maxRetries) {
      console.warn("⚠️ WebSocket max retries reached, giving up");
      this.notifyConnectionListeners("failed");
      return;
    }

    // Clear existing reconnection timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Calculate delay with exponential backoff
    const baseDelay = WS_CONFIG.reconnectInterval;
    const backoffDelay = baseDelay * Math.pow(WS_CONFIG.retryBackoffMultiplier, this.retryCount);
    const delay = Math.min(backoffDelay, WS_CONFIG.maxRetryDelay);

    this.retryCount++;
    console.log(`🔄 Scheduling WebSocket reconnection attempt ${this.retryCount}/${WS_CONFIG.maxRetries} in ${delay}ms`);
    this.notifyConnectionListeners("reconnecting");

    this.reconnectTimeout = setTimeout(() => {
      if (this.shouldReconnect && this.authData) {
        this.connect(this.authData);
      }
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    console.log("🔌 Disconnecting WebSocket");
    this.shouldReconnect = false;
    this.isConnected = false;
    this.isConnecting = false;

    // Clear timers
    this.stopPingInterval();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close WebSocket connection
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    // Notify connection listeners
    this.notifyConnectionListeners("disconnected");
  }

  /**
   * Add event listener for specific event types
   * @param {string} eventType - Type of event to listen for
   * @param {Function} callback - Callback function to execute
   * @returns {Function} - Cleanup function to remove listener
   */
  addEventListener(eventType, callback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }

    this.eventListeners.get(eventType).add(callback);

    // Return cleanup function
    return () => {
      this.removeEventListener(eventType, callback);
    };
  }

  /**
   * Remove event listener
   * @param {string} eventType - Type of event
   * @param {Function} callback - Callback function to remove
   */
  removeEventListener(eventType, callback) {
    if (this.eventListeners.has(eventType)) {
      this.eventListeners.get(eventType).delete(callback);

      // Clean up empty event type
      if (this.eventListeners.get(eventType).size === 0) {
        this.eventListeners.delete(eventType);
      }
    }
  }

  /**
   * Add connection status listener
   * @param {Function} callback - Callback function that receives connection status
   * @returns {Function} - Cleanup function to remove listener
   */
  addConnectionListener(callback) {
    this.connectionListeners.add(callback);

    // Immediately call with current status
    if (this.isConnected) {
      callback("connected");
    } else if (this.isConnecting) {
      callback("connecting");
    } else {
      callback("disconnected");
    }

    // Return cleanup function
    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  /**
   * Dispatch custom event to listeners
   * @param {string} eventType - Type of event
   * @param {any} data - Event data
   */
  dispatchCustomEvent(eventType, data) {
    if (this.eventListeners.has(eventType)) {
      const listeners = this.eventListeners.get(eventType);

      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in WebSocket event listener for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Notify connection status listeners
   * @param {string} status - Connection status
   */
  notifyConnectionListeners(status) {
    this.connectionListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error("❌ Error in WebSocket connection listener:", error);
      }
    });
  }

  /**
   * Send custom message to WebSocket server
   * @param {Object} message - Message to send
   */
  sendMessage(message) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn("⚠️ Cannot send message: WebSocket not connected");
      return false;
    }

    try {
      const messageWithTimestamp = {
        ...message,
        timestamp: message.timestamp || new Date().toISOString()
      };

      this.socket.send(JSON.stringify(messageWithTimestamp));
      return true;
    } catch (error) {
      console.error("❌ Error sending WebSocket message:", error);
      return false;
    }
  }

  /**
   * Get current connection status
   * @returns {string} - Connection status
   */
  getConnectionStatus() {
    if (this.isConnected) return "connected";
    if (this.isConnecting) return "connecting";
    return "disconnected";
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean} - True if connected
   */
  isWebSocketConnected() {
    return this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const globalWebSocketService = new GlobalWebSocketService();

// Export singleton instance and class for testing
export { globalWebSocketService as default, GlobalWebSocketService };
